// src/components/media/ZoomPanLightbox.tsx
import { useEffect, useRef, useState, useCallback } from "react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  X, ChevronLeft, ChevronRight, ZoomIn, ZoomOut, RotateCcw, Download, Ruler,
} from "lucide-react";
import { cn } from "@/lib/utils";

export interface LightboxPhoto {
  id: string;
  preview: string;
  name: string;
  category: string;
  capturedAt: string;
  source: "consultation" | "migration";
}

export interface WallMeasurements {
  wallA?: { width?: number; height?: number } | null;
  wallB?: { width?: number; height?: number } | null;
  wallC?: { width?: number; height?: number } | null;
}

type PhotoEstimations = Record<number, { loading: boolean; result: string | null }>;

interface ZoomPanLightboxProps {
  photos: LightboxPhoto[];
  initialIndex: number;
  open: boolean;
  onClose: () => void;
  /** Recorded wall measurements from job.projectConfig — shown in sidebar for measurement photos */
  wallMeasurements?: WallMeasurements | null;
  baseWidth?: number | null;
  baseLength?: number | null;
  estimations?: PhotoEstimations;
  onEstimate?: (index: number, imageUrl: string) => void;
}

const MIN_ZOOM = 1;
const MAX_ZOOM = 5;
const ZOOM_STEP = 0.25;

const CATEGORY_LABEL: Record<string, string> = {
  project_area: "Project Area",
  measurement: "Measurement",
  obstruction: "Obstruction",
  imported: "Imported",
};

export function ZoomPanLightbox({
  photos,
  initialIndex,
  open,
  onClose,
  wallMeasurements,
  baseWidth,
  baseLength,
  estimations = {},
  onEstimate,
}: ZoomPanLightboxProps) {
  const [index, setIndex] = useState(initialIndex);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const dragRef = useRef<{ startX: number; startY: number; panX: number; panY: number } | null>(null);
  const isDragging = useRef(false);

  // Reset zoom/pan when photo changes or dialog opens
  useEffect(() => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
  }, [index, open]);

  const handleEstimate = () => {
    if (!photo || !onEstimate) return;
    onEstimate(index, photo.preview);
  };

  // Sync initialIndex when dialog opens
  useEffect(() => {
    if (open) setIndex(initialIndex);
  }, [open, initialIndex]);

  const photo = photos[index] ?? null;
  const isMeasurement = photo?.category === "measurement";
  const estimation = estimations[index] ?? null;
  const showSidebar = (isMeasurement && !!(wallMeasurements || baseWidth || baseLength)) || !!(estimation?.loading || estimation?.result);

  const goTo = useCallback((next: number) => {
    const clamped = Math.max(0, Math.min(photos.length - 1, next));
    setIndex(clamped);
  }, [photos.length]);

  const adjustZoom = useCallback((delta: number) => {
    setZoom((z) => {
      const next = Math.round(Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, z + delta)) * 100) / 100;
      if (next === MIN_ZOOM) setPan({ x: 0, y: 0 });
      return next;
    });
  }, []);

  const resetView = () => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
  };

  // Keyboard navigation
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft") goTo(index - 1);
      else if (e.key === "ArrowRight") goTo(index + 1);
      else if (e.key === "+" || e.key === "=") adjustZoom(ZOOM_STEP);
      else if (e.key === "-") adjustZoom(-ZOOM_STEP);
      else if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, index, goTo, adjustZoom, onClose]);

  // Mouse wheel zoom — attached to window (non-passive) so preventDefault works.
  // Safe because the lightbox is full-screen; cleaned up when closed.
  useEffect(() => {
    if (!open) return;
    const handler = (e: WheelEvent) => {
      e.preventDefault();
      adjustZoom(e.deltaY < 0 ? ZOOM_STEP : -ZOOM_STEP);
    };
    window.addEventListener("wheel", handler, { passive: false });
    return () => window.removeEventListener("wheel", handler);
  }, [open, adjustZoom]);

  // Drag to pan
  const handleMouseDown = (e: React.MouseEvent) => {
    if (zoom <= 1) return;
    e.preventDefault();
    isDragging.current = false;
    dragRef.current = { startX: e.clientX, startY: e.clientY, panX: pan.x, panY: pan.y };
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!dragRef.current) return;
    isDragging.current = true;
    const dx = e.clientX - dragRef.current.startX;
    const dy = e.clientY - dragRef.current.startY;
    setPan({ x: dragRef.current.panX + dx, y: dragRef.current.panY + dy });
  };

  const handleMouseUp = () => {
    dragRef.current = null;
  };

  // Double-click: toggle 1× ↔ 2.5×
  const handleDoubleClick = () => {
    if (zoom > 1) {
      resetView();
    } else {
      setZoom(2.5);
    }
  };

  const handleDownload = () => {
    if (!photo) return;
    const a = document.createElement("a");
    a.href = photo.preview;
    a.download = photo.name;
    a.target = "_blank";
    a.click();
  };

  if (!photo) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="p-0 gap-0 [&>button]:hidden max-w-none w-screen h-screen bg-black/95 flex flex-col border-0 rounded-none">
        <DialogTitle className="sr-only">Photo Lightbox</DialogTitle>
        {/* Top bar */}
        <div className="flex items-center justify-between px-4 py-2 shrink-0 bg-black/60 z-10">
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="text-[10px] border-white/20 text-white/70">
              {CATEGORY_LABEL[photo.category] ?? photo.category}
            </Badge>
            <span className="text-[11px] text-white/60 truncate max-w-[200px]">{photo.name}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-[11px] text-white/50 tabular-nums">{index + 1} / {photos.length}</span>
            <span className="text-[11px] text-white/50 tabular-nums px-2 py-0.5 rounded bg-white/10">{zoom.toFixed(1)}×</span>
            <Button size="icon" variant="ghost" className="h-7 w-7 text-white/60 hover:text-white hover:bg-white/10" onClick={() => adjustZoom(ZOOM_STEP)}>
              <ZoomIn className="h-3.5 w-3.5" />
            </Button>
            <Button size="icon" variant="ghost" className="h-7 w-7 text-white/60 hover:text-white hover:bg-white/10" onClick={() => adjustZoom(-ZOOM_STEP)}>
              <ZoomOut className="h-3.5 w-3.5" />
            </Button>
            <Button size="icon" variant="ghost" className="h-7 w-7 text-white/60 hover:text-white hover:bg-white/10" onClick={resetView} title="Reset zoom">
              <RotateCcw className="h-3.5 w-3.5" />
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className={cn(
                "h-7 gap-1 px-2 text-[10px] font-semibold transition-colors",
                photo.category === "measurement"
                  ? "text-primary/80 hover:text-primary hover:bg-primary/10"
                  : "text-white/60 hover:text-white hover:bg-white/10",
                estimation?.loading && "opacity-50 cursor-not-allowed"
              )}
              disabled={estimation?.loading}
              onClick={handleEstimate}
              title="Estimate dimensions with OpenAI Vision"
            >
              <Ruler className="h-3.5 w-3.5" />
              Estimate
            </Button>
            <Button size="icon" variant="ghost" className="h-7 w-7 text-white/60 hover:text-white hover:bg-white/10" onClick={handleDownload}>
              <Download className="h-3.5 w-3.5" />
            </Button>
            <Button size="icon" variant="ghost" className="h-7 w-7 text-white/60 hover:text-white hover:bg-white/10" onClick={onClose}>
              <X className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>

        {/* Main area */}
        <div className="flex flex-1 min-h-0">
          {/* Image area */}
          <div
            className="flex-1 relative overflow-hidden flex items-center justify-center"
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
            onDoubleClick={handleDoubleClick}
            style={{ cursor: zoom > 1 ? (isDragging.current ? "grabbing" : "grab") : "default" }}
          >
            <img
              src={photo.preview}
              alt={photo.name}
              draggable={false}
              style={{
                transform: `scale(${zoom}) translate(${pan.x / zoom}px, ${pan.y / zoom}px)`,
                transformOrigin: "center",
                maxWidth: "100%",
                maxHeight: "100%",
                objectFit: "contain",
                userSelect: "none",
                transition: dragRef.current ? "none" : "transform 0.15s ease",
              }}
            />
            {/* Prev arrow */}
            {index > 0 && (
              <button
                onClick={() => goTo(index - 1)}
                className="absolute left-3 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-black/50 hover:bg-black/80 flex items-center justify-center text-white transition-colors"
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
            )}
            {/* Next arrow */}
            {index < photos.length - 1 && (
              <button
                onClick={() => goTo(index + 1)}
                className="absolute right-3 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-black/50 hover:bg-black/80 flex items-center justify-center text-white transition-colors"
              >
                <ChevronRight className="h-5 w-5" />
              </button>
            )}
          </div>

          {/* Sidebar — recorded measurements + AI estimation */}
          {showSidebar && (
            <div className="w-80 shrink-0 bg-black/60 border-l border-white/10 p-6 flex flex-col gap-4 overflow-y-auto">

              {/* OpenAI estimation — loading or result */}
              {(estimation?.loading || estimation?.result) && (
                <div className="space-y-2 pb-3 border-b border-white/10">
                  <p className="text-[11px] uppercase tracking-wider text-primary/70 font-semibold flex items-center gap-1.5">
                    <Ruler className="h-3.5 w-3.5" /> OpenAI Estimate
                  </p>
                  {estimation?.loading ? (
                    <p className="text-[18px] text-white/80 font-medium tracking-wide">
                      Generating
                      <span className="inline-flex ml-1">
                        <span className="animate-bounce [animation-delay:0ms] [animation-duration:900ms]">.</span>
                        <span className="animate-bounce [animation-delay:200ms] [animation-duration:900ms]">.</span>
                        <span className="animate-bounce [animation-delay:400ms] [animation-duration:900ms]">.</span>
                      </span>
                    </p>
                  ) : (
                    <>
                      <p className="text-[15px] text-white font-medium leading-relaxed">{estimation!.result}</p>
                      <p className="text-[11px] text-white/30">Measurements are approximate</p>
                    </>
                  )}
                </div>
              )}

              {isMeasurement && !!(wallMeasurements || baseWidth || baseLength) && (
                <>
              <p className="text-[11px] uppercase tracking-wider text-white/40 font-semibold">Recorded Measurements</p>
              <p className="text-[11px] text-white/30 leading-snug">Cross-reference with photo</p>
              <div className="space-y-2 mt-1">
                {wallMeasurements?.wallA?.width && wallMeasurements.wallA.height ? (
                  <div className="text-[11px] text-white/70">
                    <span className="text-white/40 mr-1">Wall A</span>
                    <span className="tabular-nums font-medium">{wallMeasurements.wallA.width}" × {wallMeasurements.wallA.height}"</span>
                  </div>
                ) : (
                  <div className="text-[11px] text-white/30">Wall A — not recorded</div>
                )}
                {wallMeasurements?.wallB?.width && wallMeasurements.wallB.height ? (
                  <div className="text-[11px] text-white/70">
                    <span className="text-white/40 mr-1">Wall B</span>
                    <span className="tabular-nums font-medium">{wallMeasurements.wallB.width}" × {wallMeasurements.wallB.height}"</span>
                  </div>
                ) : (
                  <div className="text-[11px] text-white/30">Wall B — not recorded</div>
                )}
                {wallMeasurements?.wallC?.width && wallMeasurements.wallC.height ? (
                  <div className="text-[11px] text-white/70">
                    <span className="text-white/40 mr-1">Wall C</span>
                    <span className="tabular-nums font-medium">{wallMeasurements.wallC.width}" × {wallMeasurements.wallC.height}"</span>
                  </div>
                ) : (
                  <div className="text-[11px] text-white/30">Wall C — not recorded</div>
                )}
                {baseWidth && baseLength ? (
                  <div className="text-[11px] text-white/70 pt-1 border-t border-white/10">
                    <span className="text-white/40 mr-1">Base</span>
                    <span className="tabular-nums font-medium">{baseWidth}" × {baseLength}"</span>
                  </div>
                ) : null}
                {!wallMeasurements?.wallA && !wallMeasurements?.wallB && !wallMeasurements?.wallC && !baseWidth ? (
                  <p className="text-[11px] text-white/30 italic">No measurements on file for this job.</p>
                ) : null}
              </div>
                </>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
